import React from "react";
import PropTypes from "prop-types";
import update from "immutability-helper";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/styles";
import { get, filter, size, map } from "lodash";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import ScrollableTable from "../Common/Table/ScrollableTable";
import ProfileVerificationHumanRow from "./ProfileVerificationHumanRow";
import ProfileVerificationVehicleRow from "./ProfileVerificationVehicleRow";

import { fetchPost } from "../../api/fetch";

import { LANGUAGES } from "../../constants/common";
import { eventGroupsData } from "../../constants/eventGroups";

import { getLov } from "../../utils/lov";
import { fetchCreatedByUsers } from "../../utils/event";

import colors from "../../assets/style/colors";
import AddVerification from "./AddVerification";

const styles = (theme) => ({
  card: {
    backgroundColor: colors.white,
    overflow: "auto",
    borderRadius: "2px",
    [theme.breakpoints.up("sm")]: {
      maxHeight: "calc(100vh - 247px)",
      height: "calc(100vh - 198px)",
      margin: "12px",
      boxShadow: "0px 2px 2px 0px " + colors.black23,
      padding: "24px",
      overflow: "hidden",
    },
    [theme.breakpoints.down("sm")]: {
      margin: "12px 6px",
      padding: "24px 10px",
      maxHeight: "calc(100vh - 160px)",
      height: "calc(100vh - 160px)",
    },
  },
  historyTable: {
    [theme.breakpoints.up("sm")]: {
      width: "calc(100vw - 166px)",
      maxWidth: "2000px",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "calc(100% - 20px)",
      overflowX: "auto",
    },
  },
  headerCell: {
    backgroundColor: colors.cardTitleBg,
    borderRight: `3px solid ${colors.white}`,
    height: "50px",
  },
  radio: {
    color: colors.black54,
    fontSize: "1em !important",
    marginRight: "40px !important",
  },
  radioRoot: {
    padding: "0px",
    marginRight: "10px",
  },
});

const columns = [
  {
    name: "profiles_verification",
    minWidth: 400,
  },
  {
    name: "profiles_dateCompleted",
    minWidth: 200,
  },
  {
    name: "profiles_location",
    minWidth: 180,
  },
  {
    name: "common_device",
    minWidth: 200,
  },
  {
    name: "profiles_results",
    minWidth: 320,
  },
  {
    name: "luke_action",
    id: 158,
    minWidth: 240,
  },
  {
    name: "comments",
    id: 19,
    minWidth: 240,
  },
  {
    name: "decision",
    id: 105,
    minWidth: 150,
  },
  {
    name: "profiles_processedBy",
    minWidth: 200,
  },
];

const views = {
  history: "history",
  group: "group",
};

const ProfileVerification = (props) => {
  const { classes, language, profile, isMe } = props;

  const [data, setData] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [lov, setLov] = React.useState();
  const [isFetching, setIsFetching] = React.useState(false);
  const [preSurveys, setPreSurveys] = React.useState();
  const [view, setView] = React.useState(views.history);


  React.useEffect(() => {
    fetchData();
    fetchLov();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLov = async () => {
    try {
      setLov({
        158: await getLov(158),
        eventTypes: await getLov("eventTypes"),
      });
    } catch (e) {
      console.log(e);
    }
  };

  function getWidth() {
    return Math.max(
      document.body.scrollWidth,
      document.documentElement.scrollWidth,
      document.body.offsetWidth,
      document.documentElement.offsetWidth,
      document.documentElement.clientWidth
    );
  }


  const fetchData = async (refresh = false) => {
    if (!isFetching) {
      try {
        setIsFetching(true);

        if (get(profile, ["type", "id"]) === 1) {
          let res = await fetchPost("new/event_groups", {
            pagination: {
              limit: 40,
              offset: refresh ? 0 : size(data),
            },
            filter: {
              types: [14],
              profile_id: get(profile, ["id"]),
            },
            data: eventGroupsData,
          });
          // Fetch created by users
          res.items = await fetchCreatedByUsers(get(res, ["items"]));

          if (refresh) {
            setData(
              filter(
                res.items,
                (v) =>
                  size(
                    filter(get(v, ["events"]), (e) =>
                      [81, 91].includes(get(e, ["type", "id"]))
                    )
                  ) > 0
              )
            );
          } else {
            setData(
              update(data, {
                $push: filter(
                  res.items,
                  (v) =>
                    size(
                      filter(get(v, ["events"]), (e) =>
                        [81, 91].includes(get(e, ["type", "id"]))
                      )
                    ) > 0
                ),
              })
            );
          }
          setTotal(res.total);
          let events91 = await fetchPost("new/events", {
            filter: {
              types: [91],
              profile_id: get(profile, ["id"]),
            },
            data: [
              "id",
              "parent_event_id",
              "active",
              "created_on",
              "created_by",
              "type",
              "profile",
              "zone",
              "event_count",
              "attributes",
              "notes",
              "images",
              "luke_index",
              "device",
              "group",
              "event_version",
              "reference_event_id",
            ],
          });
          setPreSurveys(events91.items);
        } else {
          let res = await fetchPost("new/events", {
            pagination: {
              limit: 40,
              offset: refresh ? 0 : size(data),
            },
            filter: {
              types: [42],
              profile_id: get(profile, ["id"]),
            },
            data: [
              "id",
              "active",
              "created_on",
              "created_by",
              "type",
              "profile",
              "zone",
              "event_count",
              "attributes",
              "notes",
              "images",
              "luke_index",
              "device",
              "parent_event_id",
              "group",
              "event_version",
              "reference_event_id",
            ],
          });
          // Fetch created by users
          res.items = await fetchCreatedByUsers(get(res, ["items"]));
          if (refresh) {
            setData(res.items);
          } else {
            setData(
              update(data, {
                $push: res.items,
              })
            );
          }
          setTotal(res.total);
        }

        setIsFetching(false);
        
      } catch (e) {
        console.log(e);
        setIsFetching(false);
      }
    }
  };

  return (
    <Box className={classes.card}>
      <Grid container direction="column">
        {get(profile, ["type", "id"]) === 1 && (
          <Grid item style={{ marginBottom: "24px" }}>
            <Grid container alignItems="center">
              <Grid item style={{ marginRight: "50px" }}>
                <AddVerification
                  profile={profile}
                  language={language}
                  original={false}
                  refreshHistory={() => fetchData(true)}
                  isMe={isMe}
                />
              </Grid>
              <Grid item>
                <RadioGroup
                  row
                  aria-label="position"
                  name="position"
                  value={view}
                  onChange={(e) => setView(e.target.value)}
                >
                  <FormControlLabel
                    value={views.history}
                    control={
                      <Radio
                        color="primary"
                        classes={{ root: classes.radioRoot }}
                      />
                    }
                    style={getWidth() < 478 ? {position:"relative", left: "15px"} : {}}
                    label={I18n.get("profiles_historyView")}
                    labelPlacement="end"
                    classes={{ label: classes.radio }}
                  />
                  <FormControlLabel
                    value={views.group}
                    control={
                      <Radio
                        color="primary"
                        classes={{ root: classes.radioRoot }}
                      />
                    }
                    style={getWidth() < 478 ? {position:"relative", left: "15px"} : {}}
                    label={I18n.get("profiles_groupView")}
                    labelPlacement="end"
                    classes={{ label: classes.radio }}
                  />
                </RadioGroup>
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid item className={classes.historyTable}>
          <ScrollableTable
            columns={columns}
            lazyLoading
            fetchData={fetchData}
            hasMore={total > size(data)}
            isFetching={isFetching}
            containerHeight={"calc(100vh - 294px)"}
            stickyHeader
          >
            {map(data, (item, index) =>
              get(profile, ["type", "id"]) === 1 ? (
                <ProfileVerificationHumanRow
                  key={index}
                  item={item}
                  index={index}
                  language={language}
                  view={view}
                  views={views}
                  preSurveys={preSurveys}
                  lov={lov}
                  profile={profile}
                />
              ) : (
                <ProfileVerificationVehicleRow
                  key={index}
                  item={item}
                  index={index}
                  language={language}
                />
              )
            )}
          </ScrollableTable>
        </Grid>
      </Grid>
    </Box>
  );
};

ProfileVerification.propTypes = {
  profile: PropTypes.object.isRequired,
  language: PropTypes.oneOf(LANGUAGES).isRequired,
};

export default withStyles(styles)(ProfileVerification);
